import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";

interface FormFieldProps {
  label: string;
  htmlFor: string;
  icon?: ReactNode;
  hint?: string;
  children?: ReactNode;
}

export function FormField({ label, htmlFor, icon, hint, children }: FormFieldProps) {
  return (
    <label className="form-field" htmlFor={htmlFor}>
      <span className="form-field__label">{label}</span>
      {icon ? (
        <div className="form-field__control">
          <span className="form-field__icon" aria-hidden>
            {icon}
          </span>
          {children}
        </div>
      ) : (
        children
      )}
      {hint ? <span className="form-field__hint">{hint}</span> : null}
    </label>
  );
}

type FormInputProps = InputHTMLAttributes<HTMLInputElement> & {
  hasIcon?: boolean;
};

export function FormInput({ hasIcon, className = "", ...props }: FormInputProps) {
  return (
    <input
      {...props}
      className={`input-base form-input min-h-[48px] text-base ${
        hasIcon ? "form-input--icon" : ""
      } ${className}`.trim()}
    />
  );
}

type FormSelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  hasIcon?: boolean;
};

export function FormSelect({ hasIcon, className = "", children, ...props }: FormSelectProps) {
  return (
    <select
      {...props}
      className={`select-base form-input min-h-[48px] text-base ${
        hasIcon ? "form-input--icon" : ""
      } ${className}`.trim()}
    >
      {children}
    </select>
  );
}

interface FormToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  id?: string;
  disabled?: boolean;
}

export function FormToggle({
  checked,
  onChange,
  label,
  description,
  id,
  disabled,
}: FormToggleProps) {
  return (
    <label
      className={`form-toggle ${checked ? "form-toggle--on" : ""} ${
        disabled ? "opacity-60 pointer-events-none" : ""
      }`}
      htmlFor={id}
    >
      <span className="form-toggle__text">
        <span className="form-toggle__label">{label}</span>
        {description ? <span className="form-toggle__desc">{description}</span> : null}
      </span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className="form-toggle__switch"
        onClick={() => onChange(!checked)}
      >
        <span className="form-toggle__knob" />
      </button>
    </label>
  );
}
